/* eslint-disable react-hooks/set-state-in-effect -- Effect resets the keyboard-navigation selectedIndex when the filtered groups list changes. */
import { Search, WifiOff, X } from 'lucide-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { GroupResponseDto } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { GroupsTable } from '@/components/groups/GroupsTable';
import { Portal } from '@/components/ui/Portal';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useGroupsWithBalancesQuery } from '@/hooks/queries/useGroups';
import { useDebounce } from '@/hooks/useDebounce';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface GroupSelectorModalProps {
  groups: GroupResponseDto[];
  onSelectGroup: (group: GroupResponseDto) => void;
  onClose: () => void;
  /** Defaults to the add-expense title. */
  title?: string;
  /** The line under the header; defaults to the add-expense notice, null hides it. */
  notice?: string | null;
  /** Only groups passing this are offered. */
  filter?: (group: GroupResponseDto) => boolean;
}

export function GroupSelectorModal({
  groups: initialGroups, // Use as fallback when offline
  onSelectGroup,
  onClose,
  title,
  notice,
  filter,
}: GroupSelectorModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Debounce the search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Check if we're offline using the hook for reactivity
  const { isOnline } = useNetworkStatus();
  const proEnabled = useFeatureFlag('pro');
  const isOffline = !isOnline;

  // Fetch all groups with balances (no pagination)
  // Don't fetch when offline - use cached/initial groups instead
  const { data, isLoading } = useGroupsWithBalancesQuery(
    { search: debouncedSearchQuery || undefined },
    !isOffline // Disable query when offline
  );

  // Use query data or fallback to initial groups when offline
  const allGroups = useMemo(() => {
    // First try to use query data
    if (data && data.length > 0) {
      return data;
    }

    // Fallback to initial groups when offline or no data
    // Filter by search query if present
    if (initialGroups.length > 0) {
      if (searchQuery) {
        const lowerSearch = searchQuery.toLowerCase();
        return initialGroups.filter(
          g =>
            g.name.toLowerCase().includes(lowerSearch) ||
            g.description?.toLowerCase().includes(lowerSearch)
        );
      }
      return initialGroups;
    }

    return [];
  }, [data, initialGroups, searchQuery]);
  const groups = useMemo(
    () => (filter ? allGroups.filter(filter) : allGroups),
    [allGroups, filter]
  );
  const noticeText =
    notice === undefined ? t('common:offline.selectGroupToAddExpense') : notice;

  // Focus search input on mount with a small delay to ensure modal is fully rendered
  useEffect(() => {
    // Use setTimeout to ensure the modal is fully rendered before focusing
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Reset selected index when groups change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [groups]);

  // Scroll selected item into view only when selectedIndex changes
  useEffect(() => {
    if (listRef.current && groups.length > 0 && groups[selectedIndex]) {
      const selectedElement = listRef.current.querySelector(
        `[data-testid="group-card-${groups[selectedIndex]?.id}"]`
      );
      // Only scroll if the element exists and selectedIndex actually changed
      if (selectedElement && selectedElement.scrollIntoView) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]); // Remove groups dependency to prevent scroll on load more

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (groups.length === 0) {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return 0;
            return (prev + 1) % groups.length;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => {
            if (prev === -1) return groups.length - 1;
            return (prev - 1 + groups.length) % groups.length;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && groups[selectedIndex]) {
            onSelectGroup(groups[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [groups, selectedIndex, onSelectGroup, onClose]
  );

  // Handle group selection via click
  const handleGroupClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const groupElement = target.closest('[data-testid^="group-card-"]');
      if (groupElement) {
        e.preventDefault();
        e.stopPropagation();
        const groupId = groupElement
          .getAttribute('data-testid')
          ?.replace('group-card-', '');
        const selectedGroup = groups.find(g => g.id === groupId);
        if (selectedGroup) {
          onSelectGroup(selectedGroup);
        }
      }
    },
    [groups, onSelectGroup]
  );

  // Handle mouse hover to update selected index
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const groupElement = target.closest('[data-testid^="group-card-"]');
      if (groupElement) {
        const groupId = groupElement
          .getAttribute('data-testid')
          ?.replace('group-card-', '');
        const index = groups.findIndex(g => g.id === groupId);
        if (index !== -1 && index !== selectedIndex) {
          setSelectedIndex(index);
        }
      }
    },
    [groups, selectedIndex]
  );

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50"
        onClick={onClose}
        data-testid="group-selector-modal-backdrop"
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          data-testid="group-selector-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="group-selector-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              {isOffline && (
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
              )}
              <div>
                <h2
                  id="group-selector-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                  {title ?? t('expenses:actions.addExpense')}
                </h2>
                {isOffline && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {t('common:offline.limitedMode')}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label={t('common:actions.close')}
              data-testid="group-selector-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {noticeText !== null && (
            <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800/30">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {noticeText}
              </p>
            </div>
          )}

          {/* Search */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('groups:searchGroups')}
                className="w-full pl-10 pr-4 py-2 text-gray-900 bg-gray-50 dark:bg-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                data-testid="group-search-input"
                aria-label={t('groups:searchGroups')}
                aria-describedby="search-hint"
              />
              {searchQuery && searchQuery !== debouncedSearchQuery && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
          </div>

          {/* Groups List */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto [&_a>div[class*='ring-2']]:bg-[#F6F5F3]! dark:[&_a>div[class*='ring-2']]:bg-gray-700/50! [&_a>div[class*='ring-2']]:ring-0!"
            onClick={handleGroupClick}
            onMouseMove={handleMouseMove}
            data-testid="groups-list"
          >
            <GroupsTable
              showProPills={proEnabled}
              groups={groups}
              loading={isLoading && groups.length === 0}
              showMemberCount={true}
              showLastActivity={true}
              className="border-0 shadow-none"
              emptyMessage={
                searchQuery ? t('groups:noGroupsFound') : t('groups:noGroups')
              }
              selectedGroupId={groups[selectedIndex]?.id}
            />
          </div>

          {/* Footer - Show keyboard hints on desktop only */}
          <div
            className="p-4 border-t border-gray-200 dark:border-gray-700"
            role="toolbar"
            aria-label="Keyboard shortcuts"
          >
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              {/* Hide keyboard navigation hints on mobile, show only on md and up */}
              <div className="hidden md:flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">
                    ↑↓
                  </kbd>
                  {t('common:navigation.navigate')}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">
                    Enter
                  </kbd>
                  {t('common:actions.select')}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">
                    Esc
                  </kbd>
                  {t('common:actions.close')}
                </span>
              </div>
              {/* Empty div on mobile to maintain layout */}
              <div className="md:hidden" />
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                data-testid="group-selector-cancel"
              >
                {t('common:actions.cancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
